import { useContext, useMemo, useRef } from "react";
import { AppContext } from "contexts/AppContext";
import useContactWrites from "./useContactWrites";
import { isContactDuplicateError } from "utils/contacts/contactWriteErrors";
import { sanitizeContactNumericFields } from "utils/contacts/sanitizeContactWrite";
import { toPersistedLocations } from "utils/locations/locationGroups";
import type firebase from "firebase/compat";
import type { AppContextProps } from "../../@types/app";
import type { ProcessedListingAddress, RealEstateContact, WholeBookDownloadSource } from "../../@types/sharedSchema";

/**
 * THE LEGACY CONTACTS BOOK — the signed-in realtor's ENTIRE contact book, held in memory.
 *
 * ⚠️ FOR THE TWO LEGACY WHOLE-BOOK TABLES ONLY: the old contacts page
 * (`/dashboard/contacts`) and the extension-send wizard's contact grid. Both render the
 * whole book as one client-side table, so they are the two screens that genuinely need it.
 *
 * ── Why everything else must stay away from it ──────────────────────────────────────
 *
 * `getAllContacts()` is not a read. Calling it starts a download of every contact the
 * realtor has (`POST /get-data/all-contacts`): one account holds 92,069, and before the
 * rest of the dashboard was moved off this array it was downloaded 529 times in one
 * measured day — 1.33 million documents — mostly to print a name beside a phone.
 *
 * A screen that is not one of the two legacy tables wants one of these instead:
 *
 *   a name beside a phone ........................ `useContactsLookup`
 *   a card or dialog about ONE contact ........... `useContactByPhone`
 *   a list, a search, a count .................... `useCrmContacts` / `useContactSearch`
 *   the home page's numbers ...................... `useContactsSummary`
 *   "which of these phones do I have" (imports) .. `useContactsPresence`
 *   any write ..................................... `useContactWrites`
 *
 * The import of this module is restricted to an explicit list of files (ESLint
 * `no-restricted-imports` in `package.json`, and `legacyBookContainment.test.ts`, which
 * fails if a file outside the list imports it). Adding a file to that list is a decision
 * to download the whole book on another screen; it should read like one in review.
 *
 * `source` names which legacy table is asking. It travels with the download to the
 * server's meter (`contacts_whole_book_rows{source}`), which is how the day this hook can
 * be deleted will be visible: when both sources read zero.
 */
export default function useLegacyContactsBook(source: WholeBookDownloadSource): {
  /** The book as it is in memory right now. EMPTY until something has armed the download. */
  allContacts: AppContextProps["allContacts"];
  /** Returns the book AND arms its download on first call. See the header. */
  getAllContacts: () => AppContextProps["allContacts"];
  isLoadingContacts: boolean;
  /** True once the download has finished — tells "not loaded yet" from "an empty book". */
  contactsLoaded: boolean;
  setAllContacts: AppContextProps["setAllContacts"];
  /** Keeps the book live while the caller is mounted. The old contacts page holds it. */
  retainContactsLiveFeed: AppContextProps["retainContactsLiveFeed"];
} {
  const { allContacts, getAllContacts, isLoadingContacts, contactsLoaded, setAllContacts, retainContactsLiveFeed } =
    useContext(AppContext);

  return {
    allContacts,
    getAllContacts: () => getAllContacts(source),
    isLoadingContacts,
    contactsLoaded,
    setAllContacts,
    retainContactsLiveFeed
  };
}

/**
 * A Firestore document reference, which is still how the two legacy tables name a contact to a
 * writer (`db.doc(contact._metadata.firestorePath)` on the old page, `doc(db, …, "contacts", phone)`
 * in the bulk dialog). Only its `id` — the contact's phone — is read; nothing writes through it.
 */
type ContactDocumentRef = firebase.firestore.DocumentReference<firebase.firestore.DocumentData>;

export type LegacyContactWrites = {
  /**
   * Create-or-update ONE contact in the signed-in user's book; "edit or create" is decided by
   * looking the contact up in the in-memory book (see the header of `useLegacyContactWrites`).
   */
  updateContact: (contactId: string, data: Record<string, unknown>) => Promise<void>;
  /** ONE patch per distinct value, applied to every contact mapped to it — the old page's bulk edit. */
  batchUpdate: (refAndUpdates: Map<ContactDocumentRef, Record<string, unknown>>) => Promise<void>;
  /** The old page's delete: one row, or the selection. */
  batchDelete: (refs: ContactDocumentRef[]) => Promise<void>;
};

/**
 * The server's per-request caps on the two bulk endpoints — `DEFAULT_ORG_SETTINGS` in the
 * server's `crmOrgSettingsRules.ts` (`maxBulkDelete` 500, `maxBulkEdit` 2,000). The old contacts
 * page can select its whole book, and the server REFUSES a request over the cap rather than
 * truncating it (a truncated bulk edit is invisible), so a selection goes out in chunks of
 * exactly the cap, one after the other. An agency that lowered its own cap gets the server's
 * refusal naming it, exactly as the CRM page does.
 */
const BULK_DELETE_CHUNK = 500;
const BULK_EDIT_CHUNK = 2_000;

/**
 * Keys a legacy caller may hand over that the server's whitelist refuses and the write loses
 * nothing by dropping: the stamps the browser writers always stripped, the identity pair the
 * document id already carries, and the mirrors the browser writers used to DERIVE here
 * (`customerType` from `customerTypes`, `customerSeriousness` from the CRM axes) together with
 * the Cloud-Function-stamped names. The server derives every one of them from the real document
 * inside `updateContactOfUser`, which is why `contactWriteRules.ts` refuses them; the edit
 * dialog's `DERIVED_FIELDS_NEVER_SENT` is the same list for the same reason.
 *
 * Anything else the server does not know is NOT dropped: it answers 400 naming the field, and a
 * loud refusal beats a save that reports success and changes nothing.
 */
const DROPPED_SILENTLY = new Set([
  "_metadata",
  "_ranking",
  "id",
  "callerPhoneNumber",
  "createdTime",
  "updatedTime",
  "lastBulkMessageReceivedTime",
  "customerType",
  "customerSeriousness",
  "contactFullName",
  "interestedInCitiesDistinct",
  "interestedInNeighborhoodsDistinct"
]);

/**
 * Deprecated free-text mirrors of `interestedInLocations`, dropped WITH a warning, as
 * `updateExistingContact` drops them: nothing derives them, so the fix is at the call site.
 */
const DROPPED_WITH_WARNING = new Set(["street", "neighborhood"]);

/**
 * A Firestore write sentinel (`FieldValue.delete()`, `arrayUnion(…)`, …) means nothing on the
 * wire — serialized, it is `{}`. No legacy caller sends one (grep-verified 2026-10-09), so this
 * is a tripwire: the delete sentinel becomes the `null` the server's validators read as "clear
 * the field", and any other one is refused by name rather than written as junk. The compat
 * sentinel wraps the modular one, so the method name sits on either object.
 */
const sentinelMethodName = (value: unknown): string | null => {
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as { _methodName?: unknown; _delegate?: { _methodName?: unknown } };
  const name = candidate._methodName ?? candidate._delegate?._methodName;
  return typeof name === "string" ? name : null;
};

/**
 * What a legacy caller handed over, as the server accepts it — and as the in-memory book is
 * patched afterwards, so the row on screen is the row the server stores.
 *
 * The last two steps are what the browser writers did to the same fields before their
 * `set(merge: true)`: numeric hygiene (`""` → `null`, which the server clears exactly as
 * `FieldValue.delete()` cleared; numeric strings → numbers; junk dropped with a warning), and
 * the location-group stamp stripped off `interestedInLocations` (the server rebuilds every entry
 * from named fields and would lose it anyway; stripping it here keeps the book's row identical
 * to the stored one).
 */
const toServerPatch = (data: Record<string, unknown>, contactId: string): Record<string, unknown> => {
  const patch: Record<string, unknown> = {};
  const deprecated: string[] = [];

  Object.entries(data ?? {}).forEach(([key, value]) => {
    if (value === undefined || DROPPED_SILENTLY.has(key)) return;
    if (DROPPED_WITH_WARNING.has(key)) {
      deprecated.push(key);
      return;
    }
    const sentinel = sentinelMethodName(value);
    if (sentinel !== null) {
      if (!/delete/i.test(sentinel)) {
        throw new Error(
          `useLegacyContactWrites: ${key} carries a Firestore ${sentinel} sentinel the server cannot apply — send the resulting value instead`
        );
      }
      patch[key] = null;
      return;
    }
    patch[key] = value;
  });

  if (deprecated.length > 0) {
    console.warn(
      `useLegacyContactWrites: dropped the deprecated ${deprecated.join(
        "/"
      )} mirror(s) — write interestedInLocations instead`,
      { contactId }
    );
  }

  // `null` is the server's clear; the sanitizer's parameter is typed for the Firestore sentinel
  // the browser writers used to hand it.
  sanitizeContactNumericFields(patch, null);
  if (Array.isArray(patch.interestedInLocations)) {
    patch.interestedInLocations = toPersistedLocations(patch.interestedInLocations as ProcessedListingAddress[]);
  }
  return patch;
};

/**
 * The bulk endpoint's empty-value rule, applied here too so the book is patched with what the
 * server wrote: an empty array and an empty or whitespace string are what an UNTOUCHED control
 * looks like, and the server DROPS them (`stripUnwritableBulkValues`) rather than writing them to
 * 2,000 contacts. `false`, `0` and an explicit `null` are values a realtor chose, and pass.
 */
const withoutEmptyBulkValues = (fields: Record<string, unknown>): Record<string, unknown> => {
  const kept: Record<string, unknown> = {};
  Object.entries(fields).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length === 0) return;
    if (typeof value === "string" && value.trim().length === 0) return;
    kept[key] = value;
  });
  return kept;
};

/**
 * THE LEGACY TABLES' OWN WRITERS — thin wrappers over `useContactWrites`, in the shapes the two
 * whole-book tables call them.
 *
 * Until 2026-10 these were `set(merge: true)` and `batch.delete()` against Firestore, run from
 * the browser: the last contact writes that were. They went with the freeze of the contact
 * document (plan D3, `docs/crm/contacts-postgres-authority-plan.md` in the server repo):
 * `firestore.rules` now denies every client write on `dashboard-users-v2/{email}/contacts/{phone}`,
 * which is what lets the record move to Postgres per owner with the Firestore copy left behind
 * as a frozen rollback — and what turns a forgotten browser write into a loud permission error
 * instead of a silent divergence. What the tables see did not change: the same three functions,
 * the same arguments, the same in-memory book afterwards. What changed is the door — every
 * request goes through `useContactWrites`, which also tells the lookup store — and three
 * consequences of it:
 *
 *  - `updateContact` still decides "edit or create" by looking the contact up in the in-memory
 *    book. That is sound only on a screen that HOLDS the book, which is why it lives here and
 *    nowhere else (a screen without one wants `useContactWrites().saveContact`, where the server
 *    decides). In the book → `/contacts/update`, patched optimistically and rolled back on
 *    failure; not in it → `/contacts/create`, and a 409 means the book was behind (a call being
 *    transcribed, another tab) — an edit after all. A create carries `contactOrigin: manual`,
 *    which the browser write never stamped.
 *  - `batchUpdate` is `/contacts/bulk-update`, one patch per selection in chunks of the server's
 *    cap. It REPLACES what it writes and DROPS empty values, exactly as the CRM page's bulk edit
 *    does (the browser batch wrote `""` through); an id the server does not know is skipped,
 *    where the batch minted a document. The book is then patched in place as before, so the
 *    table repaints now.
 *  - `batchDelete` is `/contacts/delete`: every document is archived into the audit ledger and
 *    THEN deleted — recoverable by script — where the browser batch was a bare hard delete.
 *
 * The functions are referentially stable, like the write hook's.
 */
export function useLegacyContactWrites(): LegacyContactWrites {
  const { allContacts, upsertContactsInMemory } = useContext(AppContext);
  const writes = useContactWrites();

  // Read through a ref so the returned functions keep one identity for the life of the
  // component while always seeing the current book — `allContacts` is a new array on every
  // change, and `upsertContactsInMemory` is rebuilt on every render of the provider.
  const current = { allContacts, upsertContactsInMemory, writes };
  const latest = useRef(current);
  latest.current = current;

  return useMemo<LegacyContactWrites>(() => {
    const updateContact: LegacyContactWrites["updateContact"] = async (contactId, data) => {
      const fields = toServerPatch(data, contactId);
      const { allContacts: book, writes: door } = latest.current;

      // The browser writer's decision, kept: both screens that reach this hold the book, so
      // "not in it" means "not a contact" here — and nowhere else.
      if (book.some((contact) => contact.id === contactId)) {
        // The write hook patches the book and the lookup store before the request and rolls
        // both back if it fails — the 🔥 toggle repaints from that patch.
        await door.updateContact(contactId, fields);
        return;
      }

      try {
        // Put into the book by the write hook as soon as the server answers, under the id the
        // server filed it (it normalizes the phone).
        await door.createContact({ contactId, fields });
      } catch (error) {
        // The book was behind — a call being transcribed, another tab, a feed window that has
        // not delivered yet. The contact exists, so this is the first branch after all.
        if (!isContactDuplicateError(error)) throw error;
        await door.updateContact(contactId, fields);
      }
    };

    const batchUpdate: LegacyContactWrites["batchUpdate"] = async (refAndUpdates) => {
      // One request per DISTINCT patch. The only caller — the old page's bulk edit — maps every
      // selected contact to the same object, so this is one group; grouping is what keeps the
      // function honest for a map that does not.
      const groups = new Map<string, { fields: Record<string, unknown>; contactIds: string[] }>();
      refAndUpdates.forEach((value, ref) => {
        const fields = withoutEmptyBulkValues(toServerPatch(value, ref.id));
        const key = JSON.stringify(fields);
        const group = groups.get(key) ?? { fields, contactIds: [] };
        group.contactIds.push(ref.id);
        groups.set(key, group);
      });

      const written: Partial<RealEstateContact>[] = [];
      for (const { fields, contactIds } of Array.from(groups.values())) {
        // A form saved with nothing in it. The browser batch wrote `{}` to every selected
        // document (a no-op that still fired the Cloud Functions); the server answers 400 to an
        // empty patch, and a refusal for "you changed nothing" is not what the realtor needs.
        if (Object.keys(fields).length === 0) continue;

        for (let start = 0; start < contactIds.length; start += BULK_EDIT_CHUNK) {
          const chunk = contactIds.slice(start, start + BULK_EDIT_CHUNK);
          const response = await latest.current.writes.bulkUpdateContacts({
            selection: { mode: "ids", contacts: chunk },
            fields
          });
          // `unchanged` already holds these values; `not_found`, `deleted` and `error` do not
          // hold them now and must not be painted as if they did.
          const untouched = new Set(
            response.skipped.filter((skip) => skip.reason !== "unchanged").map((skip) => skip.contactId)
          );
          chunk
            .filter((contactId) => !untouched.has(contactId))
            .forEach((contactId) => written.push({ id: contactId, ...fields } as Partial<RealEstateContact>));
        }
      }

      // Then the book, as the browser batch patched it after its commits: a field merge onto
      // every row it holds, so the table repaints now. The write hook has also invalidated the
      // book (the mirror's row can differ from the patch), so the old page re-downloads once
      // behind this — the patch is what the realtor sees meanwhile, the download is what
      // corrects it.
      latest.current.upsertContactsInMemory(written);
    };

    const batchDelete: LegacyContactWrites["batchDelete"] = async (refs) => {
      const contactIds = Array.from(new Set(refs.map((ref) => ref.id)));
      for (let start = 0; start < contactIds.length; start += BULK_DELETE_CHUNK) {
        // Archived into the audit ledger first, then really deleted — recoverable by script,
        // where the browser batch was a bare hard delete. The write hook removes the ids from
        // the book and marks them absent in the lookup store as each chunk answers; a contact
        // the server could not delete stays on screen.
        await latest.current.writes.deleteContacts({
          selection: { mode: "ids", contacts: contactIds.slice(start, start + BULK_DELETE_CHUNK) }
        });
      }
    };

    return { updateContact, batchUpdate, batchDelete };
  }, []);
}
